/**
 * „Sport, příroda a zážitky po celý rok" (verze 2, podle návrhu) — vlevo
 * patkový titulek, vpravo fotkové dlaždice na šířku s popiskem vlevo dole.
 * Sportoviště a sauna otevřou rovnou rezervaci, rybolov a výlety sjedou na
 * záložku v sekci Volný čas.
 */
import { BOOKING_TYPES, openBooking } from '../lib/booking'
import { useWebContent } from '../lib/webPrices'
import { assetUrl } from '../lib/api'

type Tile =
  | { title: string; image: string; pos?: string; book: string }
  | { title: string; image: string; pos?: string; href: string }

// Tenis = fotka od Robina (24. 10.), gal_14 = plážový volejbal.
// Pořadí = co se prodává nejvíc (Robin 24. 10.): beach a tenis do popředí.
const SPORT: Tile[] = [
  { title: 'Plážový volejbal', image: '/img/gal_14.jpg',                   book: 'beach' },
  { title: 'Tenis',            image: '/img/tenis_01_ctyrhra.webp',        book: 'tenis' },
  { title: 'Plovoucí sauna',   image: '/img/tinyhouse_03_sauna_dalka.jpg', book: 'sauna' },
]
const MORE: Tile[] = [
  { title: 'Rybolov',          image: '/img/rybolov_kapr_noc.jpg',         href: '#rybolov' },   // sekce Rybolov: chatky, bivak, karavan (Robin 24. 10.)
  { title: 'Výlety v okolí',   image: '/img/gal_new_24.jpg',               href: '#volny-cas-rodina' },
]

const SERIF = { fontFamily: "'Instrument Serif',Georgia,serif", fontWeight: 400 } as const

// Robin 5. 11. 2026: „okna na rezervace sport bych zvětšil" → sport velké dlaždice 4:3 přes celou
// šířku s výrazným tlačítkem, rybolov a výlety pod nimi jako dvě široké dlaždice.
function TileView({ t, wide = false }: { t: Tile; wide?: boolean }) {
  const inner = (
    <>
      <img src={t.image} alt={t.title} style={t.pos ? { objectPosition: t.pos } : undefined} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 flex items-end justify-between gap-3">
        <div className="text-white font-bold leading-tight drop-shadow text-[22px] md:text-[24px]">{t.title}</div>
        <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2 text-[14px] font-semibold transition-colors ${'book' in t ? 'bg-brand-500 text-white group-hover:bg-brand-600' : 'bg-white/90 text-ink-900 group-hover:bg-white'}`}>
          {'book' in t ? 'Rezervovat' : 'Zobrazit'} <span aria-hidden="true">→</span>
        </span>
      </div>
    </>
  )
  const cls = `group relative ${wide ? 'aspect-[16/7]' : 'aspect-[4/3]'} rounded-xl overflow-hidden block text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500`
  return 'book' in t ? (
    <button type="button" onClick={() => openBooking(BOOKING_TYPES[t.book])} className={cls}>{inner}</button>
  ) : (
    <a href={t.href} className={cls}>{inner}</a>
  )
}

const KEY: Record<string, string> = { 'Plážový volejbal': 'beach', 'Tenis': 'tenis', 'Plovoucí sauna': 'sauna', 'Rybolov': 'rybolov', 'Výlety v okolí': 'vylety' }

export function Activities() {
  const content = useWebContent()
  const adm = (t: Tile): Tile => { const k = KEY[t.title]; if (!k) return t; return { ...t, title: (content[`activity_title_${k}`] as string) || t.title, image: assetUrl(content[`activity_image_${k}`] as string) || t.image } }
  return (
    <section id="aktivity" className="py-12 md:py-16 scroll-mt-24">
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-7">
          <div>
            <p className="h-eyebrow">Aktivity v Sobáčově</p>
            <h2 className="mt-2 text-[34px] md:text-[42px] leading-[1.05] text-ink-900 tracking-tight" style={SERIF}>
              Sport, příroda a&nbsp;zážitky po&nbsp;celý rok
            </h2>
          </div>
          <div className="lg:max-w-md lg:text-right">
            <p className="text-ink-700 text-[15px] leading-relaxed">
              {(content.activities_lead as string) || 'Kurty a sauna se rezervují online, rybářskou povolenku vystavíme na místě.'}
            </p>
            <a href="#volny-cas" className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-600 hover:text-brand-700">
              Všechny aktivity <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>

        {/* sport (rezervace online) velké dlaždice; beach a tenis první (prodávají nejvíc) */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SPORT.map((t) => <TileView key={t.title} t={adm(t)} />)}
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          {MORE.map((t) => <TileView key={t.title} t={adm(t)} wide />)}
        </div>
      </div>
    </section>
  )
}
